import { useMutation, useQueryClient } from "@tanstack/react-query";

import useToastPromise from "../../../../../hooks/useToast";
import { createCard } from "../../../../../services/apiCards";

export function useCreateCardMutation({
  boardId,
  boardName,
  isTodo,
  parentCardId,
  milestone,
  onSuccess,
  onError,
}) {
  const queryClient = useQueryClient();
  const { showToastPromise } = useToastPromise();

  return useMutation({
    mutationFn: async (data) => showToastPromise(createCard, data),
    onMutate: async (data) => {
      const previousCards = queryClient.getQueryData(["cards", boardId]);

      await queryClient.cancelQueries({ queryKey: ["cards", boardId] });

      const tempCardId = '';
      const optimisticCard = {
        card: {
          card_id: tempCardId,
          title: data.title,
          workflow_step_id: isTodo ? null : data.workflow_step_id,
          is_todo: isTodo || false,
          todo_column: isTodo ? "Open" : null,
          epic_id: data.epic_id || null,
          parent_card_id: data.parent_card_id || null,
          date_created: new Date().toISOString(),
          priority: "Medium",
          allocated_hours: null,
          description: null,
          due_date: null,
          reporter: null,
          assigned_to_user: null,
          card_sequence: -1,
          board_id: boardId,
          card_type: null,
          label_ids: [],
          labels: [],
          can_exceed_allocated_hours: true,
          severity: 3,
          updated_at: new Date().toISOString(),
        },
        labels: [],
        sub_tasks: [],
        total_hours_spent: 0,
        board_permission: boardName?.board_permission || {},
        board_admin: boardName?.board_admin || null,
        board_name: boardName?.name || "",
        key: boardName?.key || "",
        assigned_user_last_seen: null,
        generic_step_id: null,
      };

      // queryClient.setQueryData(["cards", boardId], (oldData) => {
      //   const currentData = oldData || previousCards;
      //   const existingCards = currentData?.data?.data?.responseData || [];

      //   if (existingCards.length === 0 && !currentData) {
      //     return {
      //       data: {
      //         data: {
      //           responseData: [optimisticCard],
      //         },
      //       },
      //     };
      //   }

      //   return {
      //     ...currentData,
      //     data: {
      //       ...currentData.data,
      //       data: {
      //         ...currentData.data.data,
      //         responseData: [optimisticCard, ...existingCards],
      //       },
      //     },
      //   };
      // });

      return { previousCards, tempCardId };
    },
    onSuccess: (response, variables, context) => {
      const newCardId = response?.data?.data?.responseData?.card?.card_id;

      const optimisticCard = {
        card: {
          card_id: newCardId,
          title: variables.title,
          workflow_step_id: isTodo ? null : variables.workflow_step_id,
          is_todo: isTodo || false,
          todo_column: isTodo ? "Open" : null,
          epic_id: variables.epic_id || null,
          parent_card_id: variables.parent_card_id || null,
          date_created: new Date().toISOString(),
          priority: "Medium",
          allocated_hours: null,
          description: null,
          due_date: null,
          reporter: null,
          assigned_to_user: null,
          card_sequence: -1,
          board_id: boardId,
          card_type: null,
          label_ids: [],
          labels: [],
          can_exceed_allocated_hours: true,
          severity: 3,
          updated_at: new Date().toISOString(),
        },
        labels: [],
        sub_tasks: [],
        total_hours_spent: 0,
        board_permission: boardName?.board_permission || {},
        board_admin: boardName?.board_admin || null,
        board_name: boardName?.name || "",
        key: boardName?.key || "",
        assigned_user_last_seen: null,
        generic_step_id: null,
      };

      queryClient.setQueryData(["cards", boardId], (oldData) => {
        if (!oldData?.data?.data?.responseData) return oldData;


        const updatedCards = [{
          ...optimisticCard,
          card: {
            ...optimisticCard.card,
            card_id: newCardId,
          },
        },...oldData.data.data.responseData]

        return {
          ...oldData,
          data: {
            ...oldData.data,
            data: {
              ...oldData.data.data,
              responseData: updatedCards,
            },
          },
        };
      });

      if (parentCardId) {
        queryClient.invalidateQueries({
          queryKey: ["subTasks", parentCardId],
        });
      }

      if (milestone) {
        queryClient.invalidateQueries({
          queryKey: ["epic_id", milestone],
        });
        queryClient.invalidateQueries({
          queryKey: ["milestone_history", milestone],
        });
      }

      queryClient.invalidateQueries({
        queryKey: ["cards", boardId],
      });

      onSuccess?.(newCardId);
    },
    onError: (error, variables, context) => {
      if (context?.previousCards) {
        queryClient.setQueryData(["cards", boardId], context.previousCards);
      } else {
        queryClient.setQueryData(["cards", boardId], (oldData) => {
          if (!oldData?.data?.data?.responseData) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              data: {
                ...oldData.data.data,
                responseData: oldData.data.data.responseData.filter(
                  (card) => card.card.card_id !== context?.tempCardId
                ),
              },
            },
          };
        });
      }

      onError?.(error);
    },
  });
}
